import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/Header';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import HomePage from './pages/HomePage';
import SearchPage from './pages/SearchPage';
import ArticlePage from './pages/ArticlePage';
import AdminPage from './pages/AdminPage';
import MobilePreviewPage from './pages/MobilePreviewPage';
import GoogleCallbackPage from './pages/GoogleCallbackPage';
import MagicLinkVerifyPage from './pages/MagicLinkVerifyPage';
import './App.css';

// Wrapper component for the landing page redirect logic
const LandingRedirect = () => {
  const { isAuthenticated, loading } = useAuth();

  // Show nothing while loading auth state
  if (loading) {
    return null;
  }

  // If authenticated, show the main app content
  if (isAuthenticated) {
    return <Navigate to="/feed" replace />;
  }

  // Show landing page for unauthenticated users
  return <LandingPage />;
};

// Auth page wrapper - redirects to feed if already authenticated
const AuthPageWrapper = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return null;
  }

  if (isAuthenticated) {
    return <Navigate to="/feed" replace />;
  }

  return children;
};

// Main app layout with header
const AppLayout = ({ children }) => {
  return (
    <div className="App">
      <Header />
      <main className="main-content">
        {children}
      </main>
    </div>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Landing page - shows for unauthenticated users, redirects to /feed for authenticated */}
      <Route path="/" element={<LandingRedirect />} />

      {/* Auth routes - redirect to feed if already authenticated */}
      <Route
        path="/login"
        element={
          <AuthPageWrapper>
            <LoginPage />
          </AuthPageWrapper>
        }
      />
      <Route
        path="/register"
        element={
          <AuthPageWrapper>
            <RegisterPage />
          </AuthPageWrapper>
        }
      />

      {/* Google OAuth callback - no auth wrapper */}
      <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />

      {/* Magic link verification - no auth wrapper, exchanges token for session */}
      <Route path="/auth/verify" element={<MagicLinkVerifyPage />} />

      {/* Protected app routes */}
      <Route
        path="/feed"
        element={
          <ProtectedRoute>
            <AppLayout>
              <HomePage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/search"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SearchPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/article/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ArticlePage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AppLayout>
              <AdminPage />
            </AppLayout>
          </AdminRoute>
        }
      />
      <Route path="/status" element={<Navigate to="/admin" replace />} />

      {/* Internal design QA — iPhone-frame preview of mobile screens (#99) */}
      <Route
        path="/dev/mobile-preview"
        element={
          <ProtectedRoute>
            <AppLayout>
              <MobilePreviewPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
